import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  ArrowTrendingUpIcon,
  BoltIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChevronDownIcon,
  CurrencyPoundIcon,
  IdentificationIcon,
  MapPinIcon,
  PencilIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import { IVehicle } from "../../../models/Vehicle";
import { VehiclePackage } from "../../../models/VehiclePackageData";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";
import useLeadByVehicleId from "../../../data/useLeadByVehicleId";
import useNotification from "../../notifications/useNotifications";
import useVehiclePackageData from "../../../data/useVehiclePackageData";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { useAuth } from "../../../contexts/AuthContext";

import DepollutionButton from "../../depollution/DepollutionButton";
import ExportOffersTableRow from "../../exports/ExportOffersTableRow";
import Modal from "../../../layouts/Modal";
import VehicleCollectionQuestions from "../../vehicles/VehicleCollectionQuestions";
import VehicleIcons from "../../shared/VehicleIcons/VehicleIcons";
import VehicleRecalls from "../../vehicles/VehicleRecalls";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";

// Heroicons doesn't ship a dedicated gauge glyph — use the trending-up
// icon as a stand-in for mileage so the hero tile still feels on-theme.
const GaugeIcon = ArrowTrendingUpIcon;

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  /** Monospace the value — handy for codes (VIN, engine code, etc.). */
  mono?: boolean;
  /** Gives the value an amber tint (used for the miles/year highlight). */
  highlight?: boolean;
  /** Renders the item as a clickable affordance. */
  onClick?: () => void;
  title?: string;
  className?: string;
  suffix?: React.ReactNode;
}

const EM_DASH = "\u2014";

const DetailField = ({
  label,
  value,
  mono,
  highlight,
  onClick,
  title,
  className,
  suffix,
}: DetailFieldProps) => {
  const clickable = typeof onClick === "function";
  const empty =
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null" ||
    value === "undefined";

  const content = (
    <>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd
        className={classNames(
          "mt-0.5 flex items-center gap-2 text-sm leading-snug",
          empty ? "text-slate-400 italic" : "text-slate-900 font-medium",
          mono ? "font-mono text-[13px]" : "",
          highlight ? "!text-emerald-700 font-semibold" : ""
        )}
      >
        <span className="min-w-0 flex-1 truncate">
          {empty ? EM_DASH : value}
        </span>
        {suffix}
      </dd>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className={classNames(
          "group flex w-full flex-col items-start rounded-lg border border-transparent bg-slate-50/70 px-3 py-2 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/70 focus:outline-none focus:ring-2 focus:ring-primary-300",
          className ?? ""
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      title={title}
      className={classNames(
        "flex flex-col rounded-lg bg-slate-50/70 px-3 py-2",
        className ?? ""
      )}
    >
      {content}
    </div>
  );
};

interface SectionProps {
  title: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  accent?: "slate" | "primary" | "emerald" | "amber" | "rose" | "violet";
  children: React.ReactNode;
}

const ACCENT_STYLES: Record<NonNullable<SectionProps["accent"]>, string> = {
  slate: "bg-slate-100 text-slate-600",
  primary: "bg-primary-50 text-primary-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  violet: "bg-violet-50 text-violet-600",
};

const Section = ({
  title,
  icon: Icon,
  accent = "slate",
  children,
}: SectionProps) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <header className="mb-3 flex items-center gap-2">
      {Icon && (
        <span
          className={classNames(
            "flex h-7 w-7 items-center justify-center rounded-lg",
            ACCENT_STYLES[accent]
          )}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
      )}
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    </header>
    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {children}
    </dl>
  </section>
);

interface HeroTileProps {
  label: string;
  value: React.ReactNode;
  sublabel?: React.ReactNode;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone?: "slate" | "primary" | "emerald" | "amber" | "rose" | "violet";
  onClick?: () => void;
}

const HERO_TONE: Record<NonNullable<HeroTileProps["tone"]>, string> = {
  slate: "from-slate-50 to-white text-slate-700 ring-slate-200",
  primary: "from-primary-50 to-white text-primary-700 ring-primary-200",
  emerald: "from-emerald-50 to-white text-emerald-700 ring-emerald-200",
  amber: "from-amber-50 to-white text-amber-700 ring-amber-200",
  rose: "from-rose-50 to-white text-rose-700 ring-rose-200",
  violet: "from-violet-50 to-white text-violet-700 ring-violet-200",
};

const HeroTile = ({
  label,
  value,
  sublabel,
  icon: Icon,
  tone = "slate",
  onClick,
}: HeroTileProps) => {
  const body = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
        {Icon && <Icon className="h-4 w-4 opacity-70" aria-hidden />}
      </div>
      <p className="mt-1 truncate text-lg font-semibold text-slate-900">
        {value}
      </p>
      {sublabel && (
        <p className="mt-0.5 truncate text-[11px] text-slate-500">{sublabel}</p>
      )}
    </>
  );

  const baseClass = classNames(
    "rounded-xl bg-gradient-to-b p-3 ring-1 ring-inset transition-colors",
    HERO_TONE[tone]
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={classNames(baseClass, "text-left hover:brightness-[0.98]")}
      >
        {body}
      </button>
    );
  }

  return <div className={baseClass}>{body}</div>;
};

const milesPerYear = (miles: number | null, year: number | null): number | null => {
  if (!miles || !year) return null;
  const age = Math.max(1, DateTime.now().year - year);
  return miles / age;
};

const formatMiles = (miles: number | null | undefined): string => {
  if (miles === null || miles === undefined) return "—";
  return `${miles.toLocaleString("en-GB")} miles`;
};

interface PartVehicleDetailsProps {
  vehicle: IVehicle;
  price?: number | null;
  collectionQuestions?: boolean;
}

export default function PartVehicleDetails({
  vehicle,
  price = null,
  collectionQuestions = false,
}: PartVehicleDetailsProps) {
  const navigate = useNavigate();
  const { lead } = useLeadByVehicleId("PartVehicleDetails");
  const { vrmRefresh, update } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle");
  const { addNotification } = useNotification();
  const { loading, claims } = useAuth();
  // Tolerate the lookup hook being unavailable in odd test contexts.
  const salvageFetcher = useVehiclePackageData(
    vehicle.id,
    "PartVehicleSalvage"
  );
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportValuesOpen, setExportValuesOpen] = useState(false);
  const [vrm, setVrm] = useState(vehicle.vrm);
  const [ewwEngineOffer, setEwwEngineOffer] = useState(
    vehicle.enginesWorldwideEngineOffer ?? ""
  );
  const [ewwVfeOffer, setEwwVfeOffer] = useState(
    vehicle.enginesWorldwideVfeOffer ?? ""
  );
  const [vlaEngineOffer, setVlaEngineOffer] = useState(
    vehicle.vlaEngineOffer ?? ""
  );
  const [vlaVfeOffer, setVlaVfeOffer] = useState(vehicle.vlaVfeOffer ?? "");
  const [isCardOpen, setIsCardOpen] = useState(true);
  const [justDriver, setJustDriver] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (
      !claims?.isAdmin &&
      !claims?.isExporter &&
      !claims?.isMechanic &&
      !claims?.isStaff &&
      claims?.isDriver
    ) {
      setJustDriver(true);
    } else {
      setJustDriver(false);
    }
  }, [loading, claims]);

  const handleUpdateVrm = () => {
    const updatedVehicle = {
      ...vehicle,
      vrm: vrm.toUpperCase().replace(/\s/g, ""),
    };

    vrmRefresh.mutate(updatedVehicle, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handleGoToLead = () => {
    lead.mutate(vehicle.id, {
      onSuccess: (lead) => {
        if (lead) {
          // eslint-disable-next-line no-console
          console.log(lead);
        }
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to lookup lead",
        });
      },
    });
  };
  void handleGoToLead;

  const openExportValuesModal = () => {
    setEwwEngineOffer(vehicle.enginesWorldwideEngineOffer ?? "");
    setEwwVfeOffer(vehicle.enginesWorldwideVfeOffer ?? "");
    setVlaEngineOffer(vehicle.vlaEngineOffer ?? "");
    setVlaVfeOffer(vehicle.vlaVfeOffer ?? "");
    setExportValuesOpen(true);
  };

  const handleUpdateExportValues = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      enginesWorldwideEngineOffer: ewwEngineOffer || null,
      enginesWorldwideVfeOffer: ewwVfeOffer || null,
      vlaEngineOffer: vlaEngineOffer || null,
      vlaVfeOffer: vlaVfeOffer || null,
    };

    update.mutate(updatedVehicle, {
      onSuccess: () => {
        setExportValuesOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  // Inline the salvage-lookup behaviour that used to live in
  // `PartVehicleSalvage` so the value slots cleanly into the new detail
  // field grid instead of rendering its own `<dl>` fragment.
  const handleFetchSalvageValue = () => {
    salvageFetcher.getVehiclePackageData.mutate(
      { vehicleId: vehicle.id, package: VehiclePackage.JustSalvage },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["lead"]);
          queryClient.invalidateQueries(["vehiclePackage", vehicle.id]);
        },
      }
    );
  };

  const salvageValue = vehicle?.valuationTradePoor ?? null;
  const salvageAvailable =
    salvageValue != null ||
    !!salvageFetcher.vehiclePackageData.data?.data?.valuationDetailsFetchedAt;
  const isFetchingSalvage =
    salvageFetcher.getVehiclePackageData.isLoading ||
    salvageFetcher.vehiclePackageData.isLoading;

  const miles = vehicle?.odometerInMiles ?? null;
  const manufactureYear = vehicle?.dvlaYearOfManufacture ?? null;
  const mpy = useMemo(
    () => milesPerYear(miles, manufactureYear),
    [miles, manufactureYear]
  );
  const isLowMileage = mpy !== null && mpy < 8000;

  const catalyticRange = useMemo(() => {
    const results = vehicle.catalyticResults;
    if (!results || results.length === 0) return null;
    const values = results.map((r) => r.value);
    return {
      min: Math.min(...values),
      avg: vehicle.catalyticAveragePrice,
      max: Math.max(...values),
    };
  }, [vehicle.catalyticResults, vehicle.catalyticAveragePrice]);

  const catalyticDisplay = catalyticRange
    ? `£${catalyticRange.min} \u00B7 £${catalyticRange.avg} \u00B7 £${catalyticRange.max}`
    : "Unavailable";

  const exportValuesDisplay = [
    `EWW Engine ${PriceFormat(vehicle.enginesWorldwideEngineOffer)}`,
    `EWW VFE ${PriceFormat(vehicle.enginesWorldwideVfeOffer)}`,
    `VLA Engine ${PriceFormat(vehicle.vlaEngineOffer)}`,
    `VLA VFE ${PriceFormat(vehicle.vlaVfeOffer)}`,
  ].join(" \u00B7 ");

  const exportOffersCount = vehicle.exportOffers?.length ?? 0;

  const yearRangeDisplay =
    vehicle.visibilityDate || vehicle.terminateDate
      ? `${formatDateTime(vehicle.visibilityDate, { year: "numeric" })} - ${formatDateTime(
          vehicle.terminateDate,
          { year: "numeric" }
        )}`
      : undefined;

  const transmissionDisplay = [
    vehicle?.combinedForwardGears ? `${vehicle.combinedForwardGears} Speed` : "",
    vehicle?.combinedTransmission ?? "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const bodyTypeDisplay = vehicle?.bodyStyleDescription
    ? `${vehicle.bodyStyleDescription}${vehicle.numberOfDoors ? ` \u00B7 ${vehicle.numberOfDoors} door` : ""}`
    : undefined;

  const collectedOnDisplay = vehicle.collectedOn
    ? formatDateTime(
        vehicle.collectedOn,
        vehicle.collectedOn.includes("T")
          ? DateTime.DATETIME_SHORT
          : DateTime.DATE_SHORT
      )
    : "Not Collected";

  return (
    <>
      {/* Modals — unchanged behaviour. */}
      <Modal open={exportsOpen} width={"max-w-2xl"} setOpen={setExportsOpen}>
        <ExportOffersTableRow vehicle={vehicle} />
      </Modal>

      <Modal open={open} setOpen={setOpen} width="max-w-xl">
        <label className="block text-sm font-medium text-slate-700">
          Enter VRM to search for
        </label>
        <input
          defaultValue={vehicle.vrm}
          onChange={(e) => setVrm(e.target.value)}
          className="my-4 w-full rounded-md border border-slate-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          type="text"
        />
        <button
          onClick={handleUpdateVrm}
          className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
        >
          Save
        </button>
      </Modal>

      <Modal
        open={exportValuesOpen}
        setOpen={setExportValuesOpen}
        width="max-w-xl"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-slate-900">
            Edit Export Values
          </h3>
          {(
            [
              { label: "EWW Engine Offer", value: ewwEngineOffer, set: setEwwEngineOffer },
              { label: "EWW VFE Offer", value: ewwVfeOffer, set: setEwwVfeOffer },
              { label: "VLA Engine Offer", value: vlaEngineOffer, set: setVlaEngineOffer },
              { label: "VLA VFE Offer", value: vlaVfeOffer, set: setVlaVfeOffer },
            ] as const
          ).map((f) => (
            <div key={f.label}>
              <label className="block text-sm font-medium text-slate-700">
                {f.label}
              </label>
              <input
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                type="number"
                step="0.01"
              />
            </div>
          ))}
          <button
            onClick={handleUpdateExportValues}
            className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            Save
          </button>
        </div>
      </Modal>

      <div className="relative">
        <div className="overflow-hidden rounded-xl bg-white shadow ring-1 ring-slate-200">
          {/* Card header */}
          <div
            className="flex cursor-pointer select-none items-center justify-between gap-2 bg-gradient-to-r from-slate-50 to-white px-4 py-4 sm:px-6"
            onClick={() => setIsCardOpen((v) => !v)}
          >
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-6 text-slate-900">
                Vehicle Details
              </h2>
              <p className="mt-0.5 truncate text-xs text-slate-500">
                Identifiers, specification & lifecycle data pulled from the
                DVLA and the yard record
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/vehicles/${vehicle.id}/edit`);
                }}
                className="rounded-md p-2 text-slate-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                title="Edit Vehicle"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <ChevronDownIcon
                className={classNames(
                  "h-6 w-6 text-slate-400 transition-transform duration-200 ease-in-out",
                  isCardOpen ? "" : "rotate-180"
                )}
              />
            </div>
          </div>

          <div
            className={classNames(
              "border-t border-slate-200 transition-all duration-300 ease-in-out",
              isCardOpen
                ? "max-h-full opacity-100"
                : "pointer-events-none max-h-0 opacity-0"
            )}
          >
            <div className="space-y-4 bg-slate-50/40 p-4 sm:p-5">
              {collectionQuestions && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <VehicleCollectionQuestions vehicle={vehicle} />
                </div>
              )}

              {!vehicle?.found && (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-5 text-center">
                  <p className="text-base font-semibold text-rose-700">
                    Vehicle not found
                  </p>
                  <p className="max-w-md text-xs text-rose-600">
                    DVLA couldn&apos;t resolve this VRM. Double-check the
                    plate and re-run the lookup.
                  </p>
                  <button
                    onClick={() => setOpen(true)}
                    className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-rose-700"
                  >
                    Find Vehicle
                  </button>
                </div>
              )}

              {/* Hero — image + at-a-glance tiles */}
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:col-span-3">
                  <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-lg bg-slate-50">
                    {vehicle?.vehicleImageUrl ? (
                      <img
                        className="h-full w-full object-contain"
                        src={vehicle.vehicleImageUrl}
                        alt={`${vehicle.combinedMake ?? ""} ${
                          vehicle.combinedModel ?? ""
                        }`.trim()}
                      />
                    ) : (
                      <CameraIcon
                        className="h-12 w-12 text-slate-300"
                        aria-hidden
                      />
                    )}
                  </div>
                  <div className="flex w-full flex-col items-center gap-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Price
                    </span>
                    <span className="text-base font-semibold text-slate-900">
                      {price ? PriceFormat(price) : EM_DASH}
                    </span>
                    {price && <VehicleIcons vehicle={vehicle} hover />}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:col-span-9">
                  <HeroTile
                    label="Year"
                    value={manufactureYear ?? EM_DASH}
                    sublabel={yearRangeDisplay ?? "No production dates"}
                    icon={CalendarDaysIcon}
                    tone="primary"
                  />
                  <HeroTile
                    label="Mileage"
                    value={formatMiles(miles)}
                    sublabel={
                      mpy != null
                        ? `${mpy.toFixed(0).toLocaleString()} mi/year${
                            isLowMileage ? " \u00B7 low" : ""
                          }`
                        : "No odometer reading"
                    }
                    icon={GaugeIcon}
                    tone={isLowMileage ? "emerald" : "slate"}
                  />
                  <HeroTile
                    label="Engine"
                    value={
                      vehicle.combinedEngineCapacity
                        ? `${vehicle.combinedEngineCapacity}cc`
                        : EM_DASH
                    }
                    sublabel={
                      vehicle.combinedFuelType || vehicle.engineModelCode
                        ? `${vehicle.combinedFuelType ?? ""}${
                            vehicle.engineModelCode
                              ? ` \u00B7 ${vehicle.engineModelCode}`
                              : ""
                          }`.trim()
                        : "Engine data pending"
                    }
                    icon={BoltIcon}
                    tone="amber"
                  />
                  <HeroTile
                    label="Salvage value"
                    value={
                      salvageAvailable
                        ? PriceFormat(salvageValue ?? 0)
                        : isFetchingSalvage
                          ? "Fetching…"
                          : "Not fetched"
                    }
                    sublabel={
                      catalyticRange
                        ? `Cat avg £${catalyticRange.avg}`
                        : "No valuation yet"
                    }
                    icon={CurrencyPoundIcon}
                    tone={salvageAvailable ? "rose" : "slate"}
                    onClick={
                      !salvageAvailable && !isFetchingSalvage
                        ? handleFetchSalvageValue
                        : undefined
                    }
                  />
                </div>
              </div>

              {/* Identity */}
              <Section
                title="Identity"
                icon={IdentificationIcon}
                accent="primary"
              >
                <DetailField
                  label="Make & Model"
                  value={
                    vehicle.combinedMake || vehicle.combinedModel
                      ? `${vehicle.combinedMake ?? ""} ${
                          vehicle.combinedModel ?? ""
                        }`.trim()
                      : null
                  }
                />
                <DetailField label="Series" value={vehicle?.modelSeries} />
                <DetailField
                  label="Stock #"
                  value={vehicle?.stockNumber}
                  mono
                />
                <DetailField
                  label="VRM"
                  value={vehicle.vrm}
                  mono
                  suffix={
                    !loading && !justDriver ? (
                      <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 shadow-sm hover:border-primary-300 hover:text-primary-700"
                      >
                        <PencilIcon className="h-3 w-3" aria-hidden />
                        Update
                      </button>
                    ) : null
                  }
                />
                <DetailField
                  label="Chassis / VIN"
                  value={vehicle?.combinedVIN}
                  mono
                />
                <DetailField
                  label="Year range"
                  value={yearRangeDisplay}
                />
              </Section>

              {/* Specification */}
              <Section
                title="Specification"
                icon={WrenchScrewdriverIcon}
                accent="violet"
              >
                <DetailField
                  label="Engine size"
                  value={
                    vehicle.combinedEngineCapacity
                      ? `${vehicle.combinedEngineCapacity}cc`
                      : null
                  }
                />
                <DetailField
                  label="Engine code"
                  value={vehicle?.engineModelCode}
                  mono
                />
                <DetailField
                  label="Fuel type"
                  value={vehicle?.combinedFuelType}
                />
                <DetailField
                  label="Transmission"
                  value={transmissionDisplay || null}
                />
                <DetailField
                  label="Transmission code"
                  value={vehicle?.vinvisTransmissionCode}
                  mono
                />
                <DetailField label="Body type" value={bodyTypeDisplay} />
                <DetailField
                  label="Colour code"
                  value={vehicle?.vinvisColourCode}
                  mono
                />
              </Section>

              {/* Mileage & MOT */}
              <Section
                title="Mileage & Compliance"
                icon={GaugeIcon}
                accent="emerald"
              >
                <DetailField
                  label="Odometer"
                  value={formatMiles(miles)}
                />
                <DetailField
                  label="Miles / year"
                  value={mpy != null ? mpy.toFixed(0) : null}
                  highlight={isLowMileage}
                />
                <DetailField
                  label="MOT expiry"
                  value={formatDateTime(
                    vehicle?.motExpiryDate,
                    DateTime.DATE_SHORT
                  )}
                />
                <DetailField
                  label="Kerb weight"
                  value={
                    vehicle.kerbWeightMin
                      ? `${vehicle.kerbWeightMin}${
                          vehicle.kerbWeightMax
                            ? ` \u2013 ${vehicle.kerbWeightMax}`
                            : ""
                        } kg`
                      : null
                  }
                />
              </Section>

              {/* Valuation & exports */}
              <Section
                title="Valuation & Exports"
                icon={CurrencyPoundIcon}
                accent="amber"
              >
                {salvageAvailable ? (
                  <DetailField
                    label="Salvage value"
                    value={PriceFormat(salvageValue ?? 0)}
                  />
                ) : (
                  <div className="flex flex-col rounded-lg bg-slate-50/70 px-3 py-2">
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Salvage value
                    </dt>
                    <dd className="mt-1">
                      <ButtonInput
                        isSubmit={false}
                        onClick={handleFetchSalvageValue}
                        label={isFetchingSalvage ? "Fetching…" : "Fetch"}
                        classes="!py-1 !px-2 !text-xs"
                      />
                    </dd>
                  </div>
                )}
                <DetailField
                  label="Catalytic converter"
                  value={catalyticDisplay}
                  title="Minimum \u00B7 Average \u00B7 Maximum"
                />
                <DetailField
                  label="Export values"
                  value={exportValuesDisplay}
                  onClick={openExportValuesModal}
                  title="Click to edit export values"
                />
                <DetailField
                  label="Export offers"
                  value={
                    exportOffersCount > 0
                      ? `${exportOffersCount} ${
                          exportOffersCount === 1 ? "offer" : "offers"
                        }`
                      : null
                  }
                  onClick={() => setExportsOpen(true)}
                  title="View export offers"
                />
              </Section>

              {/* Lifecycle */}
              <Section
                title="Lifecycle"
                icon={MapPinIcon}
                accent="slate"
              >
                <DetailField
                  label="Collected on"
                  value={collectedOnDisplay}
                />
                <div className="flex flex-col rounded-lg bg-slate-50/70 px-3 py-2">
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Depollution
                  </dt>
                  <dd className="mt-1">
                    <DepollutionButton
                      vehicleId={vehicle.id}
                      depollutionId={vehicle.depollutionId}
                      openInNewTab
                    />
                  </dd>
                </div>
              </Section>
            </div>
          </div>

          {vehicle?.found && (
            <div className="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
              <VehicleRecalls vehicle={vehicle} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

